import { useState } from 'react'
import { useParams, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useApiClient } from '@/shared/api'
import { useUserData } from "@/entities/user";

type TaskType =
  | 'setup_instance'
  | 'run_command'
  | 'destroy_instance'
  | 'upgrade_services'
  | 'restart_services'
  | 'update_pjsip'
  | 'sync_voices'

type TaskStatus = 'new' | 'in_progress' | 'completed' | 'failed'
type StackType = 'asterisk' | 'controller'

interface SerializedTask {
  id: number
  instanceDbId: number
  taskType: TaskType
  taskParams: Record<string, unknown> | null
  status: TaskStatus
  output: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
}

interface TasksData {
  campaignId: number
  campaignName: string
  instance: {
    id: number
    stack: StackType
  }
  tasks: SerializedTask[]
}

function useInstanceTasks(campaignId: number, instanceId: number) {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: ['instance-tasks', campaignId, instanceId],
    queryFn: async () => {
      return apiClient<TasksData>(`/dialer/campaign/instance/tasks?campaignId=${campaignId}&instanceId=${instanceId}`)
    },
    enabled: !!campaignId && !!instanceId,
    refetchInterval: 5000, // Poll every 5 seconds for task updates
  })
}

function formatDate(date: string | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleString()
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>
    case 'failed':
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Failed</Badge>
    case 'in_progress':
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>
    case 'new':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatTaskType(taskType: string): string {
  return taskType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

interface ParsedTaskOutput {
  exitCode?: number
  stdout?: string
  stderr?: string
  message?: string
  instanceId?: string
  publicIp?: string
  privateIp?: string
  setupScript?: {
    exitCode?: number
    stdout?: string
    stderr?: string
  }
}

function TaskOutputDisplay({ output }: { output: string | null }) {
  if (!output) return null

  let parsed: ParsedTaskOutput | null = null
  let isPlainText = false

  try {
    parsed = JSON.parse(output)
  } catch {
    isPlainText = true
  }

  // Plain text output (error messages, etc.)
  if (isPlainText) {
    return (
      <div className="space-y-3">
        <pre className="text-detail font-mono bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-96 whitespace-pre-wrap">
          {output}
        </pre>
      </div>
    )
  }

  if (!parsed) return null

  // Determine if this is a setup_instance output (has setupScript) or simple output (has exitCode at top level)
  const isSetupOutput = !!parsed.setupScript
  const exitCode = isSetupOutput ? parsed.setupScript?.exitCode : parsed.exitCode
  const stdout = isSetupOutput ? parsed.setupScript?.stdout : parsed.stdout
  const stderr = isSetupOutput ? parsed.setupScript?.stderr : parsed.stderr
  const isSuccess = exitCode === 0

  return (
    <div className="space-y-4">
      {/* Instance Info (for setup tasks) */}
      {(parsed.message || parsed.instanceId || parsed.publicIp) && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <h4 className="font-medium text-blue-900 mb-2">Instance Information</h4>
          <div className="grid grid-cols-2 gap-2 text-body">
            {parsed.message && (
              <div className="col-span-2">
                <span className="text-blue-700">Message:</span>{' '}
                <span className="text-blue-900">{parsed.message}</span>
              </div>
            )}
            {parsed.instanceId && (
              <div>
                <span className="text-blue-700">Instance ID:</span>{' '}
                <span className="font-mono text-blue-900">{parsed.instanceId}</span>
              </div>
            )}
            {parsed.publicIp && (
              <div>
                <span className="text-blue-700">Public IP:</span>{' '}
                <span className="font-mono text-blue-900">{parsed.publicIp}</span>
              </div>
            )}
            {parsed.privateIp && (
              <div>
                <span className="text-blue-700">Private IP:</span>{' '}
                <span className="font-mono text-blue-900">{parsed.privateIp}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Exit Code Status */}
      {exitCode !== undefined && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded ${
            isSuccess ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          {isSuccess ? (
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className={`font-medium ${isSuccess ? 'text-green-800' : 'text-red-800'}`}>
            Exit Code: {exitCode}
          </span>
          <span className={`text-body ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
            {isSuccess ? '(Success)' : '(Failed)'}
          </span>
        </div>
      )}

      {/* Standard Output */}
      {stdout && stdout.trim() && (
        <div>
          <h4 className="text-body font-medium text-gray-700 mb-2 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Output
          </h4>
          <pre className="text-detail font-mono bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap">
            {stdout}
          </pre>
        </div>
      )}

      {/* Standard Error */}
      {stderr && stderr.trim() && (
        <div>
          <h4 className="text-body font-medium text-amber-700 mb-2 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Warnings / Errors
          </h4>
          <pre className="text-detail font-mono bg-amber-950 text-amber-100 p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap">
            {stderr}
          </pre>
        </div>
      )}

      {/* No output case */}
      {!stdout?.trim() && !stderr?.trim() && exitCode === undefined && !parsed.message && (
        <pre className="text-detail font-mono bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-96 whitespace-pre-wrap">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )}
    </div>
  )
}

interface TaskRowProps {
  task: SerializedTask
}

function TaskRow({ task }: TaskRowProps) {
  const [expanded, setExpanded] = useState(false)
  const hasOutput = task.output && task.output.length > 0

  return (
    <>
      <TableRow
        className={hasOutput ? 'cursor-pointer hover:bg-gray-50' : ''}
        onClick={() => hasOutput && setExpanded(!expanded)}
      >
        <TableCell className="font-mono">{task.id}</TableCell>
        <TableCell>{formatTaskType(task.taskType)}</TableCell>
        <TableCell>{getStatusBadge(task.status)}</TableCell>
        <TableCell>{formatDate(task.createdAt)}</TableCell>
        <TableCell>{formatDate(task.completedAt)}</TableCell>
        <TableCell>
          {hasOutput && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && hasOutput && (
        <TableRow>
          <TableCell colSpan={6} className="bg-gray-50 p-0">
            <div className="p-4">
              <TaskOutputDisplay output={task.output} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function getStackLabel(stack: StackType): string {
  return stack === 'asterisk' ? 'Asterisk' : 'Controller'
}

function NoAccessBlock() {
  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <div className="text-center">
        <h2 className="text-heading font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You need organization admin access to view this page.</p>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-8 w-32" />
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

export default function InstanceTasksPage() {
  const { campaignId, instanceId } = useParams<{ campaignId: string; instanceId: string }>()
  const campaignIdNum = parseInt(campaignId || '0')
  const instanceIdNum = parseInt(instanceId || '0')

  const { role } = useUserData();
  const isOrgAdmin = role === 'org:admin'

  const { data, isLoading } = useInstanceTasks(campaignIdNum, instanceIdNum)

  if (!isOrgAdmin) {
    return <NoAccessBlock />
  }

  if (isLoading || !data) {
    return <LoadingSkeleton />
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link to={`/dialer/${data.campaignId}/admin`}>
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Admin
          </Button>
        </Link>
        <div>
          <h1 className="text-heading font-bold">{getStackLabel(data.instance.stack)} Tasks</h1>
          <p className="text-gray-500">{data.campaignName}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Task History</CardTitle>
          <CardDescription>
            View all tasks for this {getStackLabel(data.instance.stack).toLowerCase()} instance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No tasks found for this instance</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">ID</TableHead>
                  <TableHead className="w-36">Task Type</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tasks.map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}